/**
 * useIdentityBrief — derives the editorial copy block at the top of the
 * User Analytics page from the active identity-pillar state.
 *
 * Mirrors `usePostureBrief` exactly: takes a `tenantId` plus the metrics it
 * needs (score, deltas, topAction, criticalCount) and calls
 * `composeIdentityBrief`. The hook makes no API calls — it is a pure
 * memoized wrapper so the standfirst stays stable across renders for a given
 * (tenant, day, input shape).
 */

import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  composeIdentityBrief,
  type IdentityBrief,
  type Pillar,
} from '@/lib/identity-brief'

export interface UseIdentityBriefArgs {
  /**
   * Active tenant ID. Combined with the local day to seed editorial variant
   * selection so the brief stays stable for that tenant across the day.
   */
  tenantId?: string
  /** MFA member adoption percent (0-100). Same number rendered on PillarIdentity. */
  score?: number | null
  /** 30-day score delta — drives the green pill on the score box. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact identity action — informs the verdict + deck risk slot. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** People needing attention — drives the named-people weak-link clause. */
  criticalCount?: number
}

export function useIdentityBrief(args: UseIdentityBriefArgs = {}): IdentityBrief {
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Memoize on the value-shape rather than the object identity so the page
  // can rebuild `args` each render without retriggering composition.
  const deltaScore = args.deltas?.score
  const deltaDays = args.deltas?.days
  const topActionPillar = args.topAction?.pillar
  const topActionTitle = args.topAction?.title
  const topActionImpact = args.topAction?.impact

  return useMemo(
    () =>
      composeIdentityBrief({
        userEmail,
        tenantId: args.tenantId,
        score: args.score,
        deltas:
          deltaScore !== undefined || deltaDays !== undefined
            ? { score: deltaScore, days: deltaDays }
            : undefined,
        topAction:
          topActionPillar && topActionTitle
            ? { pillar: topActionPillar, title: topActionTitle, impact: topActionImpact }
            : undefined,
        criticalCount: args.criticalCount,
      }),
    [
      userEmail,
      args.tenantId,
      args.score,
      deltaScore,
      deltaDays,
      topActionPillar,
      topActionTitle,
      topActionImpact,
      args.criticalCount,
    ],
  )
}
